import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventoryService } from 'services/inventory';
import { useProductService } from 'services/products';
import { useInventorySourceService } from 'services/inventorySource';
import { ROUTES } from 'utils/constants';
import { Product } from 'types/product';
import { InventorySource } from 'types/inventory';
import Button from 'components/Button';
import Toast from 'components/Toast';
import InventorySourceSearchInput from 'components/InventorySourceSearchInput';
import { ArrowLeft, X, Search, PackagePlus } from 'lucide-react';
import styles from './AddInventory.module.scss';

interface ProductEntry {
  id: string;
  productId: string;
  productName: string;
  quantity: string;
  costPerUnit: string;
  reason: string;
}

function AddInventory() {
  const navigate = useNavigate();
  const inventoryService = useInventoryService();
  const productService = useProductService();
  const inventorySourceService = useInventorySourceService();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [inventorySources, setInventorySources] = useState<InventorySource[]>(
    [],
  );
  const [productEntries, setProductEntries] = useState<ProductEntry[]>([]);
  const [sourceCategoryId, setSourceCategoryId] = useState('');
  const [date, setDate] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showAddMoreSearch, setShowAddMoreSearch] = useState(false);
  const [addMoreSearchQuery, setAddMoreSearchQuery] = useState('');
  const [isAddMoreSearchFocused, setIsAddMoreSearchFocused] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Load products and inventory sources
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [productsData, sourcesData] = await Promise.all([
          productService.getAllProducts(false),
          inventorySourceService.getAllInventorySources(),
        ]);

        // Filter only products that track inventory
        const inventoryProducts = productsData.filter(
          (product) => product.trackInventory && !product.isVoided,
        );
        setProducts(inventoryProducts);
        setInventorySources(sourcesData);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load data. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [productService, inventorySourceService, showToast]);

  // Filter products based on search
  // Show all products if search is empty, otherwise filter by query
  const getFilteredProducts = useCallback(
    (searchQuery: string) => {
      return products.filter((product) => {
        // Exclude products that are already added
        if (productEntries.some((entry) => entry.productId === product.id)) {
          return false;
        }

        // If search query is empty, show all available products
        if (!searchQuery.trim()) {
          return true;
        }

        // Otherwise, filter by search query
        const query = searchQuery.toLowerCase();
        return (
          product.name.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query)
        );
      });
    },
    [products, productEntries],
  );

  const filteredProducts = getFilteredProducts(productSearchQuery);
  const filteredAddMoreProducts = getFilteredProducts(addMoreSearchQuery);

  const handleAddProduct = useCallback(
    (product: Product, fromAddMore: boolean = false) => {
      // Check if product is already added
      if (productEntries.some((entry) => entry.productId === product.id)) {
        showToast('Product already added', 'error');
        return;
      }

      const newEntry: ProductEntry = {
        id: `${Date.now()}-${Math.random()}`,
        productId: product.id,
        productName: product.name,
        quantity: '',
        costPerUnit: '',
        reason: '',
      };

      setProductEntries((prev) => [...prev, newEntry]);
      if (fromAddMore) {
        setAddMoreSearchQuery('');
        setShowAddMoreSearch(false);
        setIsAddMoreSearchFocused(false);
      } else {
        setProductSearchQuery('');
        setIsSearchFocused(false);
      }
    },
    [productEntries, showToast],
  );

  const handleRemoveProduct = useCallback((entryId: string) => {
    setProductEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  }, []);

  const handleUpdateEntry = useCallback(
    (entryId: string, field: keyof ProductEntry, value: string) => {
      setProductEntries((prev) =>
        prev.map((entry) =>
          entry.id === entryId ? { ...entry, [field]: value } : entry,
        ),
      );
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    // Validation
    if (productEntries.length === 0) {
      showToast('Please add at least one product', 'error');
      return;
    }

    if (!sourceCategoryId) {
      showToast('Please select an inventory source', 'error');
      return;
    }

    // Validate all entries
    for (const entry of productEntries) {
      if (!entry.quantity || Number(entry.quantity) <= 0) {
        showToast(
          `Please enter a valid quantity for ${entry.productName}`,
          'error',
        );
        return;
      }

      if (!entry.costPerUnit || Number(entry.costPerUnit) <= 0) {
        showToast(
          `Please enter a valid cost per unit for ${entry.productName}`,
          'error',
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Submit all products sequentially
      const submitDate = date || new Date().toISOString().slice(0, 16);
      const isoDate = new Date(submitDate).toISOString();

      const selectedSource = inventorySources.find(
        (s) => s.id === sourceCategoryId,
      );
      if (!selectedSource) {
        throw new Error('Please select a valid inventory source');
      }

      for (const entry of productEntries) {
        await inventoryService.addStock(entry.productId, {
          quantity: Number(entry.quantity),
          sourceCategoryId: selectedSource.category.id,
          costPerUnit: Number(entry.costPerUnit),
          reason: entry.reason.trim() || undefined,
          date: isoDate,
        });
      }

      showToast(
        `Successfully added inventory for ${productEntries.length} product(s)!`,
        'success',
      );

      // Navigate back after a short delay
      setTimeout(() => {
        navigate(ROUTES.INVENTORY.ROOT);
      }, 1500);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to add inventory. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    productEntries,
    sourceCategoryId,
    date,
    inventorySources,
    inventoryService,
    showToast,
    navigate,
  ]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!products.length) {
    return (
      <div className={styles.container}>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <div className={styles.header}>
          <button
            onClick={() => navigate(ROUTES.INVENTORY.ROOT)}
            className={styles.backButton}
            aria-label="Back to inventory"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className={styles.title}>New Receipt Entry</h1>
        </div>

        <div className={styles.emptyState}>
          <h2 className={styles.emptyTitle}>No products available</h2>
          <p className={styles.emptyDescription}>
            You need at least one product that tracks inventory before you can
            add a receipt entry. Create or update a product to enable inventory
            tracking.
          </p>
          <div className={styles.emptyActions}>
            <Button
              type="button"
              onClick={() => navigate(ROUTES.PRODUCTS.ADD)}
              variant="secondary"
            >
              Go to Products
            </Button>
            <Button
              type="button"
              onClick={() => navigate(ROUTES.INVENTORY.ROOT)}
              variant="ghost"
            >
              Back to Inventory
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <button
          onClick={() => navigate(ROUTES.INVENTORY.ROOT)}
          className={styles.backButton}
          aria-label="Back to inventory"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className={styles.title}>New Receipt Entry</h1>
      </div>

      <div className={styles.logbook}>
        {/* Receipt Header */}
        <div className={styles.receiptHeader}>
          <div className={styles.receiptMeta}>
            <div className={styles.metaField}>
              <div className={styles.metaFieldRow}>
                <span className={styles.metaLabel}>Source:</span>
                <InventorySourceSearchInput
                  sources={inventorySources}
                  value={sourceCategoryId}
                  onChange={setSourceCategoryId}
                  placeholder="Cash Purchase, Supplier Credit..."
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.sourceNote}>
                Can&apos;t find the inventory source you need? Ask a company
                admin to add inventory sources for your workspace.
              </div>
            </div>
            <div className={styles.metaField}>
              <span className={styles.metaLabel}>Date:</span>
              <input
                type="datetime-local"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={styles.dateInput}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        {/* Product Search */}
        <div className={styles.productSearch}>
          <div className={styles.searchInputWrapper}>
            <Search size={16} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Add product..."
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                setTimeout(() => setIsSearchFocused(false), 200);
              }}
              className={styles.searchInput}
            />
          </div>
          {(isSearchFocused || productSearchQuery.trim()) &&
            filteredProducts.length > 0 && (
              <div className={styles.productSuggestions}>
                {filteredProducts.slice(0, 10).map((product) => (
                  <button
                    key={product.id}
                    className={styles.productSuggestionItem}
                    onClick={() => {
                      handleAddProduct(product);
                      setProductSearchQuery('');
                      setIsSearchFocused(false);
                    }}
                    type="button"
                  >
                    <div>
                      <div className={styles.productSuggestionName}>
                        {product.name}
                      </div>
                      {product.description && (
                        <div className={styles.productSuggestionDescription}>
                          {product.description}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
                {filteredProducts.length > 10 && (
                  <div className={styles.moreResults}>
                    {filteredProducts.length - 10} more available
                  </div>
                )}
              </div>
            )}
          {isSearchFocused &&
            !productSearchQuery.trim() &&
            filteredProducts.length === 0 && (
              <div className={styles.productSuggestions}>
                <div className={styles.noResults}>All products added</div>
              </div>
            )}
        </div>

        {/* Product Entries - Table-like layout */}
        {productEntries.length > 0 && (
          <div className={styles.entriesTable}>
            <div className={styles.tableHeader}>
              <div className={styles.colProduct}>Product</div>
              <div className={styles.colQty}>Qty</div>
              <div className={styles.colCost}>Cost/Unit</div>
              <div className={styles.colTotal}>Total</div>
              <div className={styles.colActions}></div>
            </div>
            <div className={styles.tableBody}>
              {productEntries.map((entry) => {
                const qty = Number(entry.quantity) || 0;
                const cost = Number(entry.costPerUnit) || 0;
                const total = qty * cost;
                return (
                  <div key={entry.id} className={styles.tableRow}>
                    <div className={styles.colProduct}>
                      <div className={styles.productName}>
                        {entry.productName}
                      </div>
                      <input
                        type="text"
                        value={entry.reason}
                        onChange={(e) =>
                          handleUpdateEntry(entry.id, 'reason', e.target.value)
                        }
                        className={styles.reasonInput}
                        placeholder="Note (optional)"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className={styles.colQty}>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={entry.quantity}
                        onChange={(e) =>
                          handleUpdateEntry(
                            entry.id,
                            'quantity',
                            e.target.value,
                          )
                        }
                        className={styles.inlineInput}
                        placeholder="0"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className={styles.colCost}>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={entry.costPerUnit}
                        onChange={(e) =>
                          handleUpdateEntry(
                            entry.id,
                            'costPerUnit',
                            e.target.value,
                          )
                        }
                        className={styles.inlineInput}
                        placeholder="0.00"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className={styles.colTotal}>
                      <span className={styles.totalAmount}>
                        {total > 0 ? `$${total.toFixed(2)}` : '-'}
                      </span>
                    </div>
                    <div className={styles.colActions}>
                      <button
                        className={styles.removeButton}
                        onClick={() => handleRemoveProduct(entry.id)}
                        aria-label={`Remove ${entry.productName}`}
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Add More Products */}
              <div className={styles.addMoreRow}>
                {!showAddMoreSearch ? (
                  <button
                    type="button"
                    className={styles.addMoreButton}
                    onClick={() => {
                      setShowAddMoreSearch(true);
                      setIsAddMoreSearchFocused(true);
                    }}
                  >
                    <PackagePlus size={14} />
                    Add product
                  </button>
                ) : (
                  <div className={styles.addMoreSearch}>
                    <div className={styles.searchInputWrapper}>
                      <Search size={14} className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={addMoreSearchQuery}
                        onChange={(e) => setAddMoreSearchQuery(e.target.value)}
                        onFocus={() => setIsAddMoreSearchFocused(true)}
                        onBlur={() => {
                          setTimeout(
                            () => setIsAddMoreSearchFocused(false),
                            200,
                          );
                        }}
                        className={styles.searchInput}
                        autoFocus
                      />
                      <button
                        type="button"
                        className={styles.closeSearchButton}
                        onClick={() => {
                          setShowAddMoreSearch(false);
                          setAddMoreSearchQuery('');
                          setIsAddMoreSearchFocused(false);
                        }}
                        aria-label="Close search"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {(isAddMoreSearchFocused || addMoreSearchQuery.trim()) &&
                      filteredAddMoreProducts.length > 0 && (
                        <div className={styles.productSuggestions}>
                          {filteredAddMoreProducts
                            .slice(0, 10)
                            .map((product) => (
                              <button
                                key={product.id}
                                className={styles.productSuggestionItem}
                                onClick={() => {
                                  handleAddProduct(product, true);
                                }}
                                type="button"
                              >
                                <div>
                                  <div className={styles.productSuggestionName}>
                                    {product.name}
                                  </div>
                                  {product.description && (
                                    <div
                                      className={
                                        styles.productSuggestionDescription
                                      }
                                    >
                                      {product.description}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>

            {/* Total Summary */}
            <div className={styles.tableFooter}>
              <div className={styles.footerLabel}>Total:</div>
              <div className={styles.footerTotal}>
                $
                {productEntries
                  .reduce((sum, entry) => {
                    const qty = Number(entry.quantity) || 0;
                    const cost = Number(entry.costPerUnit) || 0;
                    return sum + qty * cost;
                  }, 0)
                  .toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {productEntries.length > 0 && (
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(ROUTES.INVENTORY.ROOT)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={isSubmitting || productEntries.length === 0}
            >
              {isSubmitting ? 'Recording...' : 'Record Entry'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddInventory;
